import { useEffect, useRef } from "react";

/**
 * ネットワーク監視のコールバック関数の型定義
 */
export interface NetworkMonitorCallbacks {
  /**
   * ネットワーク切断時のコールバック
   */
  onDisconnect?: () => void;
  /**
   * ネットワーク再接続時のコールバック
   */
  onReconnect?: () => void;
}

/**
 * ネットワーク接続状態を監視するカスタムフック
 *
 * ブラウザのオンライン/オフラインイベントだけでなく、
 * Fetch APIのエラーもネットワークエラーとして検知させる
 *
 * @param callbacks ネットワーク状態変化時のコールバック関数
 * @returns void
 */
export const useNetworkMonitor = (callbacks?: NetworkMonitorCallbacks) => {
  // 最新のコールバックを参照するためのref
  const callbacksRef = useRef(callbacks);
  // エラーが既に検知されたかどうかのフラグ
  const hasTriggeredError = useRef(false);

  // コールバックが更新されたら ref を更新
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    /**
     * オフライン状態になった時のハンドラ
     */
    const handleOffline = () => {
      if (!hasTriggeredError.current) {
        hasTriggeredError.current = true;
        callbacksRef.current?.onDisconnect?.();
      }
    };

    /**
     * オンライン状態に戻った時のハンドラ
     * (現時点では未使用だが、将来的な拡張のために保持)
     */
    const handleOnline = () => {
      hasTriggeredError.current = false;
      callbacksRef.current?.onReconnect?.();
    };

    /**
     * エラーメッセージを抽出
     * @param event ErrorEvent または PromiseRejectionEvent
     * @returns エラーメッセージ文字列
     */
    const extractErrorMessage = (event: ErrorEvent | PromiseRejectionEvent): string => {
      if (event instanceof ErrorEvent) {
        return event.message || "";
      }

      if ("reason" in event) {
        const reason = event.reason;
        if (reason instanceof Error) {
          return reason.message;
        }
        if (typeof reason === "string") {
          return reason;
        }
      }

      return "";
    };

    /**
     * ネットワークエラーかどうかを判定する
     * @param message エラーメッセージ文字列
     * @returns ネットワークエラーならtrue
     */
    const isNetworkError = (message: string): boolean => {
      const patterns = [
        /failed to fetch/i,
        /network error/i,
        /networkerror/i,
        /net::err_/i,
        /connection/i,
      ];
      return patterns.some((pattern) => pattern.test(message));
    };

    /**
     * グローバルエラーハンドラ
     * Fetch APIやtRPCのネットワークエラーを検知
     * @param event ErrorEvent または PromiseRejectionEvent
     * @returns void
     */
    const handleError = (event: ErrorEvent | PromiseRejectionEvent) => {
      if (hasTriggeredError.current) {
        return;
      }

      const errorMessage = extractErrorMessage(event);

      // ネットワークエラーの場合にのみ処理を実行
      if (isNetworkError(errorMessage)) {
        hasTriggeredError.current = true;
        callbacksRef.current?.onDisconnect?.();
      }
    };

    // イベントリスナーを登録
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleError);

    // 初回マウント時にネットワーク状態をチェック
    if (!navigator.onLine) {
      handleOffline();
    }

    // クリーンアップ
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleError);
    };
  }, []); // マウント時のみ実行
};
